import React, { useState } from "react";
import { css } from "@emotion/css";
import { FiCalendar } from "react-icons/fi";
import EventActionButton from "./EventActionButton";
import EventQrPassCard from "./EventQrPassCard";
import { getUserKey } from "../../utils/auth";
import { config } from "../../config";
import { toast } from "react-toastify";
import { useUser } from "../../context/UserContext";

// --- Theme: gold #f5b940, purple #49255d, dashboard bg #f4f4f9 ---
const eventCardStyle = css`
    display: flex;
    flex-direction: column;
    background-color: #ffffff;
    border-radius: 12px;
    padding: 16px 18px;
    box-shadow: 0 4px 14px rgba(73, 37, 93, 0.08);
    border: 1px solid #e8e4ef;
    @media (min-width: 768px) {
        padding: 18px 22px;
    }
`;

const eventContentStyle = css`
    padding: 16px;
    display: flex;
    flex-direction: column;
    flex: 1;
    @media (min-width: 768px) {
        flex-direction: row;
        justify-content: space-between;
        align-items: center;
    }
`;

const eventInfoStyle = css`
    display: flex;
    flex-direction: column;
    margin-bottom: 16px;
    @media (min-width: 768px) {
        margin-bottom: 0;
    }
`;

const eventTitleStyle = css`
    font-size: 18px;
    font-weight: 700;
    color: #49255d;
    margin: 0 0 4px 0;
`;

const eventTimeStyle = css`
    font-size: 14px;
    color: #5a5266;
    margin: 0;
`;

const eventCancelStyle = css`
    font-size: 14px;
    color: #d32f2f;
    margin-top: 4px;
    font-weight: 600;
`;

const eventActionContainerStyle = css`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 12px;
    flex-shrink: 0;
    margin-top: 16px;
    @media (min-width: 768px) {
        margin-top: 0;
        justify-content: flex-end;
    }
`;

const modeBadgeStyle = css`
    display: inline-flex;
    align-items: center;
    padding: 2px 8px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.03em;
`;

const whatsappLinkStyle = css`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    margin-top: 8px;
    font-size: 14px;
    font-weight: 600;
    color: #25d366;
    text-decoration: none;
    cursor: pointer;
    transition: color 0.2s;
    &:hover {
        color: #128c7e;
        text-decoration: underline;
    }
`;

const eventStatusTextStyle = css`
    font-size: 14px;
    font-weight: 600;
    color: #5a5266;
    @media (min-width: 768px) {
        text-align: right;
    }
`;

const showPassButtonStyle = css`
    width: 100%;
    padding: 10px 24px;
    border-radius: 6px;
    border: none;
    background-color: #f5b940;
    color: #1f1f1f;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: background-color 0.2s;
    white-space: nowrap;
    display: flex;
    justify-content: center;
    align-items: center;

    @media (min-width: 768px) {
        width: auto;
        min-width: 140px;
    }

    &:hover:not(:disabled) {
        background-color: #dca332;
        color: #1f1f1f;
    }

    &:disabled {
        cursor: not-allowed;
        background-color: #e5e0d8;
        color: #6b6560;
    }
`;

const EventDetails = ({ event, fetchEvents }) => {
    const { profile } = useUser() || {};
    const [isPassVisible, setIsPassVisible] = useState(false);
    const [isPassLoading, setIsPassLoading] = useState(false);
    const [passData, setPassData] = useState(null);
    const [isEnrolling, setIsEnrolling] = useState(false);

    const {
        isSubscribed,
        title,
        start_date,
        start_time,
        is_cancelled,
        cancel_reason,
        amount,
        event_mode,
        event_type,
        category_name,
        category_color,
        is_paid,
        spots_left,
        is_full,
        short_description,
        venue_city,
        venue_address,
        is_booking_open,
        status,
        enroll_status,
        event_status,
        whatsappGroupLink,
    } = event;

    const isEventActive =
        typeof status === "string" && status.toLowerCase() === "published";

    const isHybrid = event_type?.toLowerCase() === "hybrid";
    const isOffline =
        event_type?.toLowerCase() === "offline" ||
        event_mode?.toLowerCase() === "offline" ||
        category_name?.toLowerCase() === "offline";

    const eventStatusLower = String(event_status || "").toLowerCase();
    const isEnrolled = isHybrid && Number(enroll_status) === 1;
    const isUpcoming = eventStatusLower === "upcoming";
    const isEnded = eventStatusLower === "ended";
    const isCancelledStatus = eventStatusLower === "cancelled";

    const showHybridEnrol = isHybrid && Number(enroll_status) === 0;
    const showHybridJoin = isHybrid && isEnrolled;
    const showHybridJoinButton = showHybridJoin && !isEnded && !isCancelledStatus;
    const isJoinDisabled = showHybridJoinButton && isUpcoming;
    const showHybridEndedText = showHybridJoin && isEnded;
    const showHybridCancelledText = showHybridJoin && isCancelledStatus;

    const handleFetchQrPass = async () => {
        if (!isEventActive) return;

        const userKey = getUserKey();
        if (!userKey || !userKey.token) {
            toast.error("Session expired. Please log in again.");
            return;
        }

        setIsPassLoading(true);
        try {
            const response = await fetch(
                `${config.serverUrl}/api/events/${event.id}/pass/qr`,
                {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${userKey.token}`,
                    },
                },
            );

            const result = await response.json();

            if (response.status === 400) {
                toast.error(
                    result?.msg || "QR pass is only valid on event date.",
                );
                setPassData(null);
                setIsPassVisible(false);
                return;
            }

            if (response.ok && result.status === "success") {
                setPassData(result.response);
                setIsPassVisible(true);
            } else {
                toast.error(result?.msg || "Failed to generate event pass.");
                setPassData(null);
                setIsPassVisible(false);
            }
        } catch (error) {
            console.error("Error fetching QR pass", error);
            toast.error("Something went wrong while loading the event pass.");
        } finally {
            setIsPassLoading(false);
        }
    };

    const handleTogglePass = () => {
        if (!isEventActive) return;

        if (isPassVisible) {
            setIsPassVisible(false);
            return;
        }

        // If we already have data, just toggle visibility.
        if (passData) {
            setIsPassVisible(true);
            return;
        }

        // First time: fetch then show.
        handleFetchQrPass();
    };

    const handleEnrolClick = async () => {
        if (!isEventActive || !event?.id) return;
        const userKey = getUserKey();
        if (!userKey?.token) {
            toast.error("Session expired. Please log in again.");
            return;
        }
        setIsEnrolling(true);
        try {
            const response = await fetch(
                `${config.serverUrl}/api/enrollments`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${userKey.token}`,
                    },
                    body: JSON.stringify({ eventId: Number(event.id) }),
                },
            );
            const result = await response.json();
            const data = result?.data;

            if (!response.ok || result?.success === false) {
                toast.error(result?.message || "Enrollment failed.");
                return;
            }
            const message = result?.message || "";

            if (data?.requiresPayment) {
                toast.info(message || "Payment required for enrollment.");
                // TODO: redirect to payment using data.eventId, data.amount, data.currency, etc.
                return;
            }
            if (data?.status === "waitlisted") {
                toast.success(message || "Added to waitlist.");
                fetchEvents();
                return;
            }
            // Free event — approved
            toast.success(message || "Enrollment successful.");
            if (data?.whatsappGroupLink) {
                toast.info(
                    "Check your enrollment details for the WhatsApp group link.",
                    { autoClose: 5000 },
                );
            }
            fetchEvents();
        } catch (err) {
            console.error("Enrollment error", err);
            toast.error("Something went wrong. Please try again.");
        } finally {
            setIsEnrolling(false);
        }
    };

    return (
        <div className={eventCardStyle}>
            <div className={eventContentStyle}>
                <div
                    className={eventInfoStyle}
                    style={{ flex: 1, paddingRight: "16px" }}
                >
                    <h3 className={eventTitleStyle}>{title}</h3>
                    {short_description && (
                        <p
                            style={{
                                fontSize: "13px",
                                color: "#5a5266",
                                margin: "4px 0 8px 0",
                                maxWidth: "400px",
                            }}
                        >
                            {short_description}
                        </p>
                    )}

                    <div
                        style={{
                            display: "flex",
                            flexWrap: "wrap",
                            alignItems: "center",
                            gap: "12px",
                            marginTop: "8px",
                        }}
                    >
                        <span
                            className={modeBadgeStyle}
                            style={{
                                backgroundColor: isOffline
                                    ? "#ede9f5"
                                    : "#fef3c7",
                                color: isOffline ? "#49255d" : "#b45309",
                            }}
                        >
                            {isOffline ? "Offline" : "Online"}
                        </span>
                        {!isHybrid && (
                            <p
                                className={eventTimeStyle}
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "4px",
                                    margin: 0,
                                }}
                            >
                                <FiCalendar size={14} />{" "}
                                {new Date(start_date).toLocaleDateString(
                                    "en-US",
                                    {
                                        day: "numeric",
                                        month: "short",
                                        year: "numeric",
                                    },
                                )}{" "}
                                at{" "}
                                {new Date(
                                    `1970-01-01T${start_time}Z`,
                                ).toLocaleTimeString("en-US", {
                                    hour: "numeric",
                                    minute: "numeric",
                                    hour12: true,
                                    timeZone: "UTC",
                                })}
                            </p>
                        )}
                        {isOffline && venue_address && (
                            <p
                                className={eventTimeStyle}
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "4px",
                                    margin: 0,
                                }}
                            >
                                <span>📍</span> {venue_address}
                                {venue_city &&
                                venue_address.indexOf(venue_city) === -1
                                    ? `, ${venue_city}`
                                    : ""}
                            </p>
                        )}
                        {(!isOffline || !venue_address) && venue_city && (
                            <p
                                className={eventTimeStyle}
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "4px",
                                    margin: 0,
                                }}
                            >
                                <span>📍</span> {venue_city}
                            </p>
                        )}
                    </div>

                    {whatsappGroupLink && (
                        <a
                            href={whatsappGroupLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={whatsappLinkStyle}
                        >
                            {isHybrid
                                ? "Join WhatsApp group of the course"
                                : "Join WhatsApp group of the event"}
                        </a>
                    )}

                    {is_cancelled && (
                        <p
                            className={eventCancelStyle}
                            style={{ marginTop: "8px" }}
                        >
                            {cancel_reason}
                        </p>
                    )}
                </div>

                <div className={eventActionContainerStyle}>
                    <EventActionButton
                        event={event}
                        fetchEvents={fetchEvents}
                        isSubscribed={isSubscribed}
                    />

                    {isEventActive && showHybridEnrol && (
                        <button
                            className={showPassButtonStyle}
                            onClick={handleEnrolClick}
                            disabled={isEnrolling}
                        >
                            {isEnrolling ? "Enrolling..." : "Enroll Course"}
                        </button>
                    )}
                    {isEventActive && showHybridJoinButton && (
                        <button
                            className={showPassButtonStyle}
                            type="button"
                            onClick={() => {
                                if (isJoinDisabled) return;
                                // TODO: implement join action (e.g. open meeting / link)
                            }}
                            disabled={isJoinDisabled}
                            title={
                                isJoinDisabled
                                    ? "Event has not started yet"
                                    : undefined
                            }
                        >
                            Start Course
                        </button>
                    )}
                    {showHybridEndedText && (
                        <span className={eventStatusTextStyle}>
                            Event ended
                        </span>
                    )}
                    {showHybridCancelledText && (
                        <span className={eventStatusTextStyle}>
                            Event cancelled
                        </span>
                    )}
                    {isEventActive && !isHybrid && (
                        <button
                            className={showPassButtonStyle}
                            onClick={handleTogglePass}
                            disabled={isPassLoading}
                        >
                            {isPassLoading
                                ? "Loading Pass..."
                                : isPassVisible
                                  ? "Hide Pass ▲"
                                  : "Show Event Pass ▼"}
                        </button>
                    )}
                </div>
            </div>
            {isEventActive && isPassVisible && passData && (
                <EventQrPassCard
                    profile={profile}
                    event={event}
                    passData={passData}
                    onRefresh={handleFetchQrPass}
                    isRefreshing={isPassLoading}
                />
            )}
        </div>
    );
};

export default EventDetails;
